// @flow

import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, useParams, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { log } from '../../utils/jsUtils';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Logo from '../../assets/askneo-favicon.png';
import { auth } from '../../services/firebase';
import { login } from '../../reduxStore/modules/user/actions';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { selectIsLoggedIn } from '../../reduxStore/modules/user/selectors';
import { selectIsAuthStateLoading } from '../../reduxStore/modules/common/selectors';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { getRequiredOnboardingStep } from '../../utils/onboardingUtils';

// Import theme
import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';
import loginStyles from './Styles/Login.Style';

const Login = () => {
  const theme = useTheme();
  const classes = createClasses(loginStyles, theme);
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Get URL parameters
  const { reset } = useParams();
  const location = useLocation();

  // Component state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [openReset, setOpenReset] = useState(reset === 'reset-password');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get authentication state from Redux
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isAuthLoading = useSelector(selectIsAuthStateLoading);

  // Handle login submission
  const handleLogin = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!email || !password) {
      dispatch(setSnackMessage({
        message: 'Please enter both email and password',
        level: 'warning'
      }));
      return;
    }

    setIsSubmitting(true);
    log('[Login] Attempting login for:', email);

    try {
      // Get user agent for tracking
      const userAgent = navigator.userAgent;
      log('[Login] User agent:', userAgent);

      // Call login action and wait for result
      const result = await dispatch(login(email, password));
      log('[Login] Login result:', result);

      if (result.success) {
        log('[Login] Login successful, checking redirect...');
        // Reset form state
        setEmail('');
        setPassword('');

        // Make sure localStorage is set (redundant but ensures it's done)
        localStorage.setItem('loggedIn', 'true');
        localStorage.setItem('authUser', JSON.stringify({ email }));

        // Check if user needs to complete onboarding before going to /member
        const { user, billing, defaultBuilding } = result.data;
        const requiredStep = getRequiredOnboardingStep({ user, billing, defaultBuilding });

        const defaultRedirect = location.state?.from?.pathname || '/member';
        const redirectTo = requiredStep || defaultRedirect;
        log('[Login] Redirecting to:', redirectTo);
        history.push(redirectTo, { replace: true });
      } else {
        // Error is already shown via snackbar from the action
        log('[Login] Login failed:', result.error);

        // Try direct Firebase auth as fallback
        try {
          log('[Login] Attempting direct Firebase login as fallback');
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;

          // If we got here, Firebase auth succeeded but Redux failed
          log('[Login] Direct Firebase login succeeded, setting localStorage');
          localStorage.setItem('loggedIn', 'true');
          localStorage.setItem('authUser', JSON.stringify({ email }));

          const redirectTo = location.state?.from?.pathname || '/member';
          log('[Login] Redirecting to:', redirectTo);
          history.push(redirectTo, { replace: true });
        } catch (fbError) {
          log('[Login] Direct Firebase login also failed:', fbError);
        }
      }
    } catch (error) {
      log('[Login] Exception during login:', error);
      dispatch(setSnackMessage({
        message: 'Login error: ' + (error.message || 'Unknown error'),
        level: 'error'
      }));
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, dispatch, history, location.state]);

  // Handle password reset
  const handleResetPassword = useCallback(async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!email) {
      dispatch(setSnackMessage({
        message: 'Please enter your email address',
        level: 'error',
        duration: 3000
      }));
      return;
    }
    
    try {
      await sendPasswordResetEmail(auth, email);
      
      dispatch(setSnackMessage({
        message: 'Password reset email sent. Please check your inbox.',
        level: 'success',
        duration: 5000
      }));
      
      setOpenReset(false);
    } catch (err) {
      dispatch(setSnackMessage({
        message: `Failed to send password reset email: ${err.message}`,
        level: 'error',
        duration: 5000
      }));
    }
  }, [email, dispatch]);

  // Only redirect if user is logged in and auth state has been determined
  if (isLoggedIn && !isAuthLoading) {
    // Prevent redirect loop - check if we're already at a protected route
    const redirectPath = location.state?.from?.pathname || '/member';
    
    // Only redirect if we're not already on the login page with a redirection in progress
    if (location.pathname === '/login') {
      log('[Login] User is logged in, redirecting to:', redirectPath);
      return <Redirect to={redirectPath} />;
    }
  }

  // Render login form
  return (
    <Grid sx={classes.contentCtr} container>
      <Helmet>
        <title>Neo | Sign in</title>
      </Helmet>

      <Grid item xs={12} sx={classes.headerLogo}>
        <a href="/"><img height={40} src={Logo} alt="AskNeo" /></a>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="h2" sx={classes.formTitle}>
          {'Sign in'}
        </Typography>
        <form onSubmit={(e) => {
          e.preventDefault();
          log('[Login] Form submitted');
          handleLogin(e);
        }}>
          <input
            autoComplete="email"
            style={{
              backgroundColor: 'white',
              borderRadius: 0,
              padding: `${theme.spacing(2)} 30px`,
              minWidth: '100%',
              textAlign: 'left',
              margin: 0,
              fontSize: '1rem',
              borderTop: '1px #B4CEE1 solid',
              borderRight: '1px #B4CEE1 solid',
              borderLeft: '1px #B4CEE1 solid',
              borderBottom: '1px #B4CEE1 solid',
              outline: 0,
              boxSizing: 'border-box'
            }}
            id="email"
            onChange={event => setEmail(event.target.value)}
            placeholder={'Email'}
            type="text"
            value={email}
            disabled={isSubmitting}
          />
          <input
            autoComplete="new-password"
            style={{
              backgroundColor: 'white',
              borderRadius: 0,
              padding: `${theme.spacing(2)} 30px`,
              minWidth: '100%',
              textAlign: 'left',
              margin: 0,
              fontSize: '1rem',
              borderTop: 0,
              borderRight: '1px #B4CEE1 solid',
              borderLeft: '1px #B4CEE1 solid',
              borderBottom: '1px #B4CEE1 solid',
              outline: 0,
              boxSizing: 'border-box'
            }}
            id="password"
            onChange={event => setPassword(event.target.value)}
            placeholder={'Password'}
            type="password"
            value={password}
            disabled={isSubmitting}
          />

          <Button
            sx={classes.signin}
            color="primary"
            type="submit"
            variant='contained'
            disabled={isSubmitting || !email || !password}
          >
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </Grid>
      
      <Grid
        item
        xs={12}
        sx={classes.helpLink}
      >
        <a href='#' onClick={(e) => { e.preventDefault(); setOpenReset(true); }}>
          {'Forgot password? Reset'}
        </a>
      </Grid>
      
      <Grid
        item
        xs={12}
        sx={classes.helpLink}
      >
        <a href='/register'>{'No account? Sign up'}</a>
      </Grid>

      <Dialog
        aria-labelledby='form-dialog-title'
        onClose={() => setOpenReset(false)}
        open={openReset}
      >
        <form
          autoComplete='off'
          noValidate
          onSubmit={handleResetPassword}
        >
          <DialogTitle id='form-dialog-title'>{'Reset your password'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {'You\'ll receive an email with instructions to reset your password.'}
            </DialogContentText>
            
            <TextField
              autoFocus
              fullWidth
              id='resetEmail'
              label='Email'
              margin='dense'
              onChange={event => setEmail(event.target.value)}
              type='email'
              value={email}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              color='primary'
              onClick={() => setOpenReset(false)}
            >
              {'Cancel'}
            </Button>
            <Button 
              color='primary'
              onClick={handleResetPassword}
              variant='contained'
              disabled={!email}
            >
              {'Reset'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Grid>
  );
};

export default Login;